import { useCallback } from 'react';

import type { ReviewTemplate } from '@/types/review/ReviewTemplate';

import { useAmelioTranslations, useNavigate } from '@/hooks';
import { useError } from '@/hooks/utils/useError';
import { showWarningToast } from '@/plugins/Toast';

interface UseSelectTemplateNavigationProps {
    selectedTemplate?: ReviewTemplate;
    questionnaireId?: string;
    hasExistingQuestions: boolean;
    navigationPath: string;
    importTemplate: (params: { questionnaireId: string; templateId: string }) => Promise<unknown>;
    updateSelectTemplateStep?: () => Promise<boolean>;
}

export const useSelectTemplateNavigation = ({
    selectedTemplate,
    questionnaireId,
    hasExistingQuestions,
    navigationPath,
    importTemplate,
    updateSelectTemplateStep,
}: UseSelectTemplateNavigationProps) => {
    const { t } = useAmelioTranslations();
    const { handleError } = useError();
    const navigate = useNavigate();

    const onNext = useCallback(
        async (isBuilding?: boolean) => {
            if (isBuilding || (hasExistingQuestions && !selectedTemplate?.id)) {
                if (updateSelectTemplateStep) {
                    const success = await updateSelectTemplateStep();
                    if (!success) {
                        return false;
                    }
                }
                navigate({ path: navigationPath });
                return true;
            }

            if (!selectedTemplate?.id) {
                showWarningToast({
                    message: t('evaluation360SelectTemplate.validation.templateValidation'),
                });

                return false;
            }

            if (!questionnaireId) {
                handleError({});

                return false;
            }

            try {
                await importTemplate({
                    questionnaireId,
                    templateId: selectedTemplate.id,
                });

                return true;
            } catch (error) {
                handleError({ error });
                return false;
            }
        },
        [
            selectedTemplate,
            questionnaireId,
            hasExistingQuestions,
            navigationPath,
            importTemplate,
            navigate,
            handleError,
            t,
        ],
    );

    return { onNext };
};
